import type { LabelType } from '@skop-app/lib-domain/types'

import { getMetadataValue } from '@/features/property/options/metadata'
import type { Metadata } from '@/services/property/property.type'

type OptionLike = {
  id: string
  label: string
  value?: string | null
  isDefault?: boolean | null
  metadata?: Metadata[]
  options?: readonly OptionLike[]
}

const DEFAULT_METADATA_KEYS = ['default', 'isDefault'] as const
const DEFAULT_TRUE_VALUES = new Set(['true', '1', 'yes'])

const hasTruthyDefaultMetadata = (
  metadata: Metadata[] | undefined,
): boolean => {
  return DEFAULT_METADATA_KEYS.some((key) => {
    const raw = getMetadataValue(metadata, key)
    if (typeof raw !== 'string') return false
    return DEFAULT_TRUE_VALUES.has(raw.trim().toLowerCase())
  })
}

export const hasDefaultOptionFlag = (
  option: Pick<OptionLike, 'isDefault' | 'metadata'>,
): boolean =>
  option.isDefault === true || hasTruthyDefaultMetadata(option.metadata)

const toValue = (propertyKey: string, option: OptionLike): string => {
  if (propertyKey === 'action') {
    return option.value ?? option.id
  }
  return option.id
}

export function findDefaultOption<T extends OptionLike>(
  propertyKey: string,
  options: readonly T[] | undefined,
): LabelType | undefined {
  if (!Array.isArray(options) || options.length === 0) return undefined

  const stack: OptionLike[] = [...options]
  while (stack.length > 0) {
    const current = stack.shift()
    if (!current) continue

    if (hasDefaultOptionFlag(current)) {
      return { label: current.label, value: toValue(propertyKey, current) }
    }

    if (Array.isArray(current.options) && current.options.length > 0) {
      stack.unshift(...current.options)
    }
  }

  return undefined
}
